import * as React from 'react'
import * as jotai from 'jotai'

import * as api from '@owl-nest/api-client/latest'
import * as hooks from '@owl-nest/hooks'

import * as base from './api'

const SUBSCRIPTION_ORDERS_CACHE = jotai.atom<hooks.Cache<api.Pageable<api.OrderList>[]>>({})

type SubscriptionOrdersParams = {
  limit?: number
}

function toURLSearchParams(
  params: SubscriptionOrdersParams = {},
  searchParams = new URLSearchParams(),
): URLSearchParams {
  if (params.limit) {
    searchParams.set('limit', String(params.limit))
  }

  return searchParams
}

type SubscriptionOrdersConfig = {
  fetchOnMount?: boolean
  initialLimit?: number
  params?: SubscriptionOrdersParams
}

type UseSubscriptionOrders = {
  response: hooks.Response<api.Pageable<api.OrderList>[]>
  query: () => Promise<void>
  queryNext: () => Promise<void>
  hasNext: boolean
}

export function useSubscriptionOrders(
  subscriptionId?: number,
  { params = {}, initialLimit = 20, fetchOnMount = true }: SubscriptionOrdersConfig = {},
): UseSubscriptionOrders {
  const sharedQuery = hooks.useSharedQuery(
    async (subscriptionId: number, params: SubscriptionOrdersParams | undefined, initialLimit: number | undefined) => {
      const response = await base.listForSubscription(
        subscriptionId,
        toURLSearchParams({ ...params, limit: initialLimit ?? params?.limit }),
      )
      return response.next((success) => [success])
    },
    {
      atom: SUBSCRIPTION_ORDERS_CACHE,
      id,
    },
  )

  hooks.useFetchOnMount({ fetchOnMount, query, getResponse }, id(subscriptionId, params))

  return { response: getResponse(), query, queryNext, hasNext: typeof getMeta(getResponse())?.next === 'string' }

  function getResponse(): hooks.Response<api.Pageable<api.OrderList>[]> {
    if (subscriptionId === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }

    return sharedQuery.getCurrentResponse([subscriptionId, params, initialLimit])
  }

  async function query(): Promise<void> {
    if (subscriptionId === undefined) {
      return undefined
    }

    return sharedQuery.query(subscriptionId, params, initialLimit)
  }

  async function queryNext(): Promise<void> {
    if (subscriptionId === undefined) {
      return
    }

    const response = getResponse()
    const next = getMeta(response)?.next ?? undefined
    const nextSearchParam = toURLSearchParams(params, new URLSearchParams(next))

    if (next === undefined) {
      return
    }

    await sharedQuery.patch([subscriptionId, params, initialLimit], async (response) => {
      const ordersResponse = await base.listForSubscription(subscriptionId, nextSearchParam)

      return ordersResponse.next((success) => {
        return [...(response.data ?? []), success]
      })
    })
  }
}

function getMeta(response: hooks.Response<api.Pageable<api.OrderList>[]>): api.Pageable<void>['meta'] | undefined {
  if (response.status === hooks.QueryStatus.PRISTINE) {
    return
  }

  const data = response?.data

  return data?.length ? data[data.length - 1].meta : undefined
}

function id(subscriptionId?: number, params?: SubscriptionOrdersParams, initialLimit?: number): string {
  return JSON.stringify([subscriptionId, params?.limit, initialLimit])
}
